import ExcelJS from "exceljs";
import { getFacilityManagementData } from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/facilityManagementData";

/**
 * Export Facility Management Dashboard Data to Excel
 * Creates tables matching the exact data structure for comparison with UI
 */
export async function exportFacilityManagementToExcel() {
  try {
    const data = getFacilityManagementData();
    const facility = data.facility;
    const monthlyData = data.monthlyData;
    const currentDate = new Date().toLocaleDateString("en-GB");

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "OneVR Analytics";
    workbook.created = new Date();
    workbook.modified = new Date();

    // ==================== FACILITY INFO SHEET ====================
    const facilitySheet = workbook.addWorksheet("Facility Info", {
      properties: { tabColor: { argb: "FF3B82F6" } }
    });

    facilitySheet.columns = [{ width: 35 }, { width: 45 }];

    // Title
    facilitySheet.mergeCells("A1:B1");
    const titleCell = facilitySheet.getCell("A1");
    titleCell.value = "FACILITY INFORMATION & BENCHMARKS";
    titleCell.font = { size: 16, bold: true, color: { argb: "FFFFFFFF" } };
    titleCell.alignment = { vertical: "middle", horizontal: "center" };
    titleCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF3B82F6" }
    };
    facilitySheet.getRow(1).height = 35;

    let row = 3;
    addDataRow(facilitySheet, row++, "Facility ID", facility.id);
    addDataRow(facilitySheet, row++, "Name", facility.name);
    addDataRow(facilitySheet, row++, "Location", facility.location);
    addDataRow(facilitySheet, row++, "Address", facility.address);
    addDataRow(facilitySheet, row++, "Total Area (sqm)", facility.totalArea);
    addDataRow(facilitySheet, row++, "Number of Floors", facility.floorsCount);
    addDataRow(facilitySheet, row++, "Floor Area (sqm)", facility.floorArea);
    addDataRow(facilitySheet, row++, "Total Assets", facility.totalAssets);
    addDataRow(
      facilitySheet,
      row++,
      "Critical Assets",
      facility.criticalAssets
    );
    addDataRow(
      facilitySheet,
      row++,
      "Maintenance Staff",
      facility.maintenanceStaff
    );
    addDataRow(facilitySheet, row++, "Vendor Count", facility.vendorCount);
    addDataRow(facilitySheet, row++, "Capacity", facility.capacity);

    row++;
    addSectionHeader(facilitySheet, row++, "Building Information");
    addDataRow(
      facilitySheet,
      row++,
      "Year Built",
      facility.buildingInfo.yearBuilt
    );
    addDataRow(
      facilitySheet,
      row++,
      "Building Type",
      facility.buildingInfo.buildingType
    );
    addDataRow(
      facilitySheet,
      row++,
      "Construction Type",
      facility.buildingInfo.constructionType
    );
    addDataRow(
      facilitySheet,
      row++,
      "Certifications",
      facility.buildingInfo.certifications.join(", ")
    );
    addDataRow(
      facilitySheet,
      row++,
      "HVAC System",
      facility.buildingInfo.hvacSystem
    );
    addDataRow(
      facilitySheet,
      row++,
      "Lighting System",
      facility.buildingInfo.lightingSystem
    );
    addDataRow(
      facilitySheet,
      row++,
      "Security System",
      facility.buildingInfo.securitySystem
    );
    addDataRow(
      facilitySheet,
      row++,
      "Elevators",
      facility.buildingInfo.elevators
    );
    addDataRow(
      facilitySheet,
      row++,
      "Parking Spaces",
      facility.buildingInfo.parkingSpaces
    );

    row++;
    addSectionHeader(facilitySheet, row++, "Operating Hours");
    addDataRow(
      facilitySheet,
      row++,
      "Building Access Start",
      facility.operatingHours.start
    );
    addDataRow(
      facilitySheet,
      row++,
      "Building Access End",
      facility.operatingHours.end
    );
    addDataRow(
      facilitySheet,
      row++,
      "Core Hours Start",
      facility.operatingHours.coreHours.start
    );
    addDataRow(
      facilitySheet,
      row++,
      "Core Hours End",
      facility.operatingHours.coreHours.end
    );
    addDataRow(
      facilitySheet,
      row++,
      "Operating Days",
      facility.operatingHours.weekdays.join(", ")
    );
    addDataRow(
      facilitySheet,
      row++,
      "Weekend Access",
      facility.operatingHours.weekendAccess
    );

    row++;
    addSectionHeader(facilitySheet, row++, "Cost Structure (€)");
    addDataRow(
      facilitySheet,
      row++,
      "Energy Cost per kWh",
      facility.costs.energyCostPerKwh
    );
    addDataRow(
      facilitySheet,
      row++,
      "Water Cost per m³",
      facility.costs.waterCostPerM3
    );
    addDataRow(
      facilitySheet,
      row++,
      "Waste Disposal per kg",
      facility.costs.wasteDisposalPerKg
    );
    addDataRow(
      facilitySheet,
      row++,
      "Monthly Maintenance Budget",
      facility.costs.maintenanceBudget
    );
    addDataRow(
      facilitySheet,
      row++,
      "Monthly Operating Budget",
      facility.costs.operatingBudget
    );

    row++;
    addSectionHeader(facilitySheet, row++, "Environmental Factors");
    addDataRow(
      facilitySheet,
      row++,
      "CO₂ Factor per kWh (kg)",
      facility.environmental.co2FactorPerKwh
    );
    addDataRow(
      facilitySheet,
      row++,
      "Water Efficiency Target (m³/person/day)",
      facility.environmental.waterEfficiencyTarget
    );
    addDataRow(
      facilitySheet,
      row++,
      "Waste Recycling Target (%)",
      facility.environmental.wasteRecyclingTarget
    );
    addDataRow(
      facilitySheet,
      row++,
      "Green Space Area (sqm)",
      facility.environmental.greenSpaceArea
    );

    row++;
    addSectionHeader(facilitySheet, row++, "Performance Benchmarks");
    addDataRow(
      facilitySheet,
      row++,
      "Equipment Uptime Target (%)",
      facility.benchmarks.equipmentUptime
    );
    addDataRow(
      facilitySheet,
      row++,
      "Energy Intensity Target (kWh/sqm)",
      facility.benchmarks.energyIntensity
    );
    addDataRow(
      facilitySheet,
      row++,
      "Space Utilization Target (%)",
      facility.benchmarks.spaceUtilization
    );
    addDataRow(
      facilitySheet,
      row++,
      "Vendor SLA Target (%)",
      facility.benchmarks.vendorSLA
    );
    addDataRow(
      facilitySheet,
      row++,
      "Predictive Accuracy Target (%)",
      facility.benchmarks.predictiveAccuracy
    );
    addDataRow(
      facilitySheet,
      row++,
      "Max Response Time (hours)",
      facility.benchmarks.responseTime
    );
    addDataRow(
      facilitySheet,
      row++,
      "Maintenance Completion Target (%)",
      facility.benchmarks.maintenanceCompletion
    );

    row++;
    addSectionHeader(facilitySheet, row++, "Asset Categories");
    addDataRow(
      facilitySheet,
      row++,
      "HVAC Systems",
      facility.assetCategories.hvac
    );
    addDataRow(
      facilitySheet,
      row++,
      "Electrical Systems",
      facility.assetCategories.electrical
    );
    addDataRow(
      facilitySheet,
      row++,
      "Plumbing Systems",
      facility.assetCategories.plumbing
    );
    addDataRow(
      facilitySheet,
      row++,
      "Elevator Components",
      facility.assetCategories.elevators
    );
    addDataRow(
      facilitySheet,
      row++,
      "Security Systems",
      facility.assetCategories.security
    );
    addDataRow(
      facilitySheet,
      row++,
      "Lighting Systems",
      facility.assetCategories.lighting
    );
    addDataRow(
      facilitySheet,
      row++,
      "Fire Safety Equipment",
      facility.assetCategories.fireSystem
    );

    row++;
    addSectionHeader(facilitySheet, row++, "Contact Information");
    addDataRow(
      facilitySheet,
      row++,
      "Facility Manager",
      facility.contacts.facilityManager
    );
    addDataRow(
      facilitySheet,
      row++,
      "Technical Lead",
      facility.contacts.technicalLead
    );
    addDataRow(
      facilitySheet,
      row++,
      "Emergency Contact",
      facility.contacts.emergencyContact
    );
    addDataRow(
      facilitySheet,
      row++,
      "Maintenance Hotline",
      facility.contacts.maintenanceHotline
    );

    // ==================== EQUIPMENT STATUS SHEET ====================
    const equipmentSheet = workbook.addWorksheet("Equipment Status", {
      properties: { tabColor: { argb: "FF22C55E" } }
    });

    equipmentSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 15 }, // Uptime %
      { width: 12 }, // Change %
      { width: 12 }, // Active Assets
      { width: 12 }, // Total Assets
      { width: 12 }, // Offline
      { width: 18 }, // Maintenance Due
      { width: 18 }, // Maintenance Scheduled
      { width: 18 }, // Maintenance Completed
      { width: 12 }, // Critical Issues
      { width: 12 }, // Warning Issues
      { width: 12 } // Info Issues
    ];

    createEquipmentStatusTable(equipmentSheet, monthlyData);

    // ==================== ASSET HEALTH SHEET ====================
    const assetHealthSheet = workbook.addWorksheet("Asset Health", {
      properties: { tabColor: { argb: "FF10B981" } }
    });

    createAssetHealthTable(assetHealthSheet, monthlyData);

    // ==================== ENERGY MANAGEMENT SHEET ====================
    const energySheet = workbook.addWorksheet("Energy Management", {
      properties: { tabColor: { argb: "FFF59E0B" } }
    });

    energySheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 18 }, // Total Consumption
      { width: 12 }, // Change %
      { width: 15 }, // Total Cost (€)
      { width: 15 }, // Cost per sqm
      { width: 12 }, // Intensity
      { width: 12 }, // Peak Demand
      { width: 12 }, // Off-Peak %
      { width: 15 }, // Renewable %
      { width: 15 }, // Efficiency Score
      { width: 15 } // CO₂ Emissions
    ];

    createEnergyManagementTable(energySheet, monthlyData);

    // ==================== ENERGY BREAKDOWN SHEET ====================
    const energyBreakdownSheet = workbook.addWorksheet("Energy Breakdown", {
      properties: { tabColor: { argb: "FFEF4444" } }
    });

    createEnergyBreakdownTable(energyBreakdownSheet, monthlyData);

    // ==================== SPACE UTILIZATION SHEET ====================
    const spaceSheet = workbook.addWorksheet("Space Utilization", {
      properties: { tabColor: { argb: "FF8B5CF6" } }
    });

    spaceSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 15 }, // Overall Rate %
      { width: 12 }, // Change %
      { width: 15 }, // Occupied Area
      { width: 15 }, // Total Area
      { width: 15 }, // Peak Time
      { width: 15 }, // Avg Density
      { width: 15 }, // Hot Desk Efficiency
      { width: 12 }, // Utilized Desks
      { width: 12 } // Available Desks
    ];

    createSpaceUtilizationTable(spaceSheet, monthlyData);

    // ==================== FLOOR UTILIZATION SHEET ====================
    const floorUtilSheet = workbook.addWorksheet("Floor Utilization", {
      properties: { tabColor: { argb: "FF06B6D4" } }
    });

    createFloorUtilizationTable(floorUtilSheet, monthlyData);

    // ==================== ENVIRONMENTAL MONITORING SHEET ====================
    const envSheet = workbook.addWorksheet("Environmental", {
      properties: { tabColor: { argb: "FF14B8A6" } }
    });

    envSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 12 }, // Temp Avg
      { width: 12 }, // Temp Min
      { width: 12 }, // Temp Max
      { width: 15 }, // Temp Compliance
      { width: 12 }, // Humidity Avg
      { width: 12 }, // Humidity Min
      { width: 12 }, // Humidity Max
      { width: 15 }, // Humidity Compliance
      { width: 12 }, // CO₂ Avg
      { width: 12 }, // CO₂ Max
      { width: 15 }, // CO₂ Compliance
      { width: 12 }, // Air Quality
      { width: 12 }, // Waste Total
      { width: 12 } // Waste Recycled
    ];

    createEnvironmentalTable(envSheet, monthlyData);

    // ==================== VENDOR PERFORMANCE SHEET ====================
    const vendorSheet = workbook.addWorksheet("Vendor Performance", {
      properties: { tabColor: { argb: "FFEC4899" } }
    });

    vendorSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 15 }, // SLA Compliance %
      { width: 12 }, // Change %
      { width: 15 }, // Avg Response Time
      { width: 15 }, // Total Spend (€)
      { width: 15 }, // Budget Utilization
      { width: 15 }, // Work Orders Total
      { width: 18 }, // Work Orders Completed
      { width: 15 }, // Completion Rate %
      { width: 12 } // Active Vendors
    ];

    createVendorPerformanceTable(vendorSheet, monthlyData);

    // ==================== TOP VENDORS SHEET ====================
    const topVendorsSheet = workbook.addWorksheet("Top Vendors", {
      properties: { tabColor: { argb: "FFF43F5E" } }
    });

    createTopVendorsTable(topVendorsSheet, monthlyData);

    // ==================== PREDICTIVE ANALYTICS SHEET ====================
    const predictiveSheet = workbook.addWorksheet("Predictive Analytics", {
      properties: { tabColor: { argb: "FFA855F7" } }
    });

    predictiveSheet.columns = [
      { width: 15 }, // Month
      { width: 12 }, // Date
      { width: 18 }, // Failure Predictions
      { width: 15 }, // Accuracy Rate %
      { width: 18 }, // Prevented Downtime
      { width: 15 }, // Cost Savings (€)
      { width: 12 }, // Risk Score
      { width: 15 }, // Risk Level
      { width: 18 }, // Next Month Forecast
      { width: 18 }, // Next Quarter Forecast
      { width: 15 } // Next Year Forecast
    ];

    createPredictiveAnalyticsTable(predictiveSheet, monthlyData);

    // ==================== UPCOMING MAINTENANCE SHEET ====================
    const maintenanceSheet = workbook.addWorksheet("Upcoming Maintenance", {
      properties: { tabColor: { argb: "FFDC2626" } }
    });

    createUpcomingMaintenanceTable(maintenanceSheet, monthlyData);

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Facility-Management-${facility.name.replace(
      /\s+/g,
      "-"
    )}-${currentDate.replace(/\//g, "-")}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, filename: link.download };
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    return { success: false, error: error.message };
  }
}

// ==================== HELPER FUNCTIONS ====================

function addDataRow(sheet, rowNum, label, value) {
  sheet.getCell(`A${rowNum}`).value = label;
  sheet.getCell(`A${rowNum}`).font = {
    bold: true,
    size: 11,
    color: { argb: "FF374151" }
  };
  sheet.getCell(`A${rowNum}`).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" }
  };

  sheet.getCell(`B${rowNum}`).value = value;
  sheet.getCell(`B${rowNum}`).font = { size: 11, color: { argb: "FF1F2937" } };

  sheet.getRow(rowNum).height = 20;
}

function addSectionHeader(sheet, rowNum, text) {
  sheet.mergeCells(`A${rowNum}:B${rowNum}`);
  const cell = sheet.getCell(`A${rowNum}`);
  cell.value = text;
  cell.font = { bold: true, size: 12, color: { argb: "FFFFFFFF" } };
  cell.alignment = { vertical: "middle", horizontal: "center" };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF6B7280" }
  };
  sheet.getRow(rowNum).height = 25;
}

function createTableHeader(sheet, headers, color) {
  headers.forEach((header, index) => {
    const cell = sheet.getCell(1, index + 1);
    cell.value = header;
    cell.font = { bold: true, size: 11, color: { argb: "FFFFFFFF" } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: color || "FF3B82F6" }
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FFE5E7EB" } },
      left: { style: "thin", color: { argb: "FFE5E7EB" } },
      bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
      right: { style: "thin", color: { argb: "FFE5E7EB" } }
    };
  });
  sheet.getRow(1).height = 30;
}

function addDataCell(sheet, rowNum, colNum, value, isNumber = false) {
  const cell = sheet.getCell(rowNum, colNum);
  cell.value = value;
  cell.font = { size: 10, color: { argb: "FF1F2937" } };
  cell.alignment = {
    vertical: "middle",
    horizontal: isNumber ? "right" : "left"
  };
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };

  if (isNumber && typeof value === "number") {
    cell.numFmt = value % 1 === 0 ? "#,##0" : "#,##0.0";
  }

  sheet.getRow(rowNum).height = 20;
}

function addPercentCell(sheet, rowNum, colNum, value) {
  const cell = sheet.getCell(rowNum, colNum);
  cell.value = value;
  cell.font = { size: 10, color: { argb: "FF1F2937" } };
  cell.alignment = { vertical: "middle", horizontal: "right" };
  cell.numFmt = "0.0%";
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };
  sheet.getRow(rowNum).height = 20;
}

function addChangeCell(sheet, rowNum, colNum, value) {
  const cell = sheet.getCell(rowNum, colNum);
  cell.value = value / 100;
  cell.numFmt = "+0.0%;-0.0%";
  cell.font = {
    size: 10,
    color: { argb: value >= 0 ? "FF10B981" : "FFEF4444" }
  };
  cell.alignment = { vertical: "middle", horizontal: "right" };
  cell.border = {
    top: { style: "thin", color: { argb: "FFE5E7EB" } },
    left: { style: "thin", color: { argb: "FFE5E7EB" } },
    bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    right: { style: "thin", color: { argb: "FFE5E7EB" } }
  };
  sheet.getRow(rowNum).height = 20;
}

// ==================== TABLE CREATION FUNCTIONS ====================

function createEquipmentStatusTable(sheet, monthlyData) {
  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "Uptime %",
      "Change %",
      "Active",
      "Total",
      "Offline",
      "Due",
      "Scheduled",
      "Completed",
      "Critical",
      "Warning",
      "Info"
    ],
    "FF22C55E"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const eq = month.equipment;

    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addPercentCell(sheet, row, 3, eq.uptimePercentage / 100);
    addChangeCell(sheet, row, 4, eq.changePercent);
    addDataCell(sheet, row, 5, eq.activeAssets, true);
    addDataCell(sheet, row, 6, eq.totalAssets, true);
    addDataCell(sheet, row, 7, eq.criticalAssets.offline, true);
    addDataCell(sheet, row, 8, eq.maintenanceDue, true);
    addDataCell(sheet, row, 9, eq.maintenanceScheduled, true);
    addDataCell(sheet, row, 10, eq.maintenanceCompleted, true);
    addDataCell(sheet, row, 11, eq.criticalAssets.critical, true);
    addDataCell(sheet, row, 12, eq.criticalAssets.warning, true);
    addDataCell(sheet, row, 13, eq.criticalAssets.info, true);
  });
}

function createAssetHealthTable(sheet, monthlyData) {
  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "HVAC",
      "Electrical",
      "Plumbing",
      "Elevators",
      "Security",
      "Lighting",
      "Fire System"
    ],
    "FF10B981"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const assets = month.equipment.assetHealth;

    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);

    assets.forEach((asset, assetIndex) => {
      addDataCell(sheet, row, assetIndex + 3, asset.score, true);
    });
  });
}

function createEnergyManagementTable(sheet, monthlyData) {
  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "Consumption (kWh)",
      "Change %",
      "Cost (€)",
      "Cost/sqm",
      "Intensity",
      "Peak (kW)",
      "Off-Peak %",
      "Renewable %",
      "Efficiency",
      "CO₂ (kg)"
    ],
    "FFF59E0B"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const energy = month.energy;

    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addDataCell(sheet, row, 3, energy.totalConsumption, true);
    addChangeCell(sheet, row, 4, energy.changePercent);
    addDataCell(sheet, row, 5, energy.totalCost, true);
    addDataCell(sheet, row, 6, energy.costPerSqm, true);
    addDataCell(sheet, row, 7, energy.intensity, true);
    addDataCell(sheet, row, 8, energy.peakDemand, true);
    addPercentCell(sheet, row, 9, energy.offPeakUsage / 100);
    addPercentCell(sheet, row, 10, energy.renewablePercentage / 100);
    addDataCell(sheet, row, 11, energy.efficiencyScore, true);
    addDataCell(sheet, row, 12, energy.co2Emissions, true);
  });
}

function createEnergyBreakdownTable(sheet, monthlyData) {
  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "HVAC %",
      "Lighting %",
      "Equipment %",
      "Elevators %",
      "Other %"
    ],
    "FFEF4444"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const breakdown = month.energy.breakdown;

    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addPercentCell(sheet, row, 3, breakdown.hvac / 100);
    addPercentCell(sheet, row, 4, breakdown.lighting / 100);
    addPercentCell(sheet, row, 5, breakdown.equipment / 100);
    addPercentCell(sheet, row, 6, breakdown.elevators / 100);
    addPercentCell(sheet, row, 7, breakdown.other / 100);
  });
}

function createSpaceUtilizationTable(sheet, monthlyData) {
  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "Overall %",
      "Change %",
      "Occupied (sqm)",
      "Total (sqm)",
      "Peak Time",
      "Avg Density",
      "Hot Desk %",
      "Utilized",
      "Available"
    ],
    "FF8B5CF6"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const space = month.spaceUtilization;

    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addPercentCell(sheet, row, 3, space.overallRate / 100);
    addChangeCell(sheet, row, 4, space.changePercent);
    addDataCell(sheet, row, 5, space.occupiedArea, true);
    addDataCell(sheet, row, 6, space.totalArea, true);
    addDataCell(sheet, row, 7, space.peakOccupancyTime);
    addDataCell(sheet, row, 8, space.averageDensity, true);
    addPercentCell(sheet, row, 9, space.hotDesking.efficiency / 100);
    addDataCell(sheet, row, 10, space.hotDesking.utilized, true);
    addDataCell(sheet, row, 11, space.hotDesking.available, true);
  });
}

function createFloorUtilizationTable(sheet, monthlyData) {
  createTableHeader(
    sheet,
    ["Month", "Date", "Floor 1 %", "Floor 2 %", "Floor 3 %", "Floor 4 %"],
    "FF06B6D4"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const floors = month.spaceUtilization.floorUtilization;

    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);

    floors.forEach((floor, floorIndex) => {
      addPercentCell(sheet, row, floorIndex + 3, floor.rate / 100);
    });
  });
}

function createEnvironmentalTable(sheet, monthlyData) {
  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "Temp Avg",
      "Temp Min",
      "Temp Max",
      "Temp OK %",
      "Humidity Avg",
      "Humidity Min",
      "Humidity Max",
      "Humidity OK %",
      "CO₂ Avg",
      "CO₂ Max",
      "CO₂ OK %",
      "Air Quality",
      "Waste (kg)",
      "Recycled (kg)"
    ],
    "FF14B8A6"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const env = month.environmental;

    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addDataCell(sheet, row, 3, env.temperature.average, true);
    addDataCell(sheet, row, 4, env.temperature.min, true);
    addDataCell(sheet, row, 5, env.temperature.max, true);
    addPercentCell(sheet, row, 6, env.temperature.compliance / 100);
    addDataCell(sheet, row, 7, env.humidity.average, true);
    addDataCell(sheet, row, 8, env.humidity.min, true);
    addDataCell(sheet, row, 9, env.humidity.max, true);
    addPercentCell(sheet, row, 10, env.humidity.compliance / 100);
    addDataCell(sheet, row, 11, env.co2Levels.average, true);
    addDataCell(sheet, row, 12, env.co2Levels.max, true);
    addPercentCell(sheet, row, 13, env.co2Levels.compliance / 100);
    addDataCell(sheet, row, 14, env.airQuality.index, true);
    addDataCell(sheet, row, 15, env.waste.total, true);
    addDataCell(sheet, row, 16, env.waste.recycled, true);
  });
}

function createVendorPerformanceTable(sheet, monthlyData) {
  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "SLA %",
      "Change %",
      "Response (h)",
      "Spend (€)",
      "Budget %",
      "Orders Total",
      "Orders Done",
      "Completion %",
      "Vendors"
    ],
    "FFEC4899"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const vendor = month.vendorPerformance;

    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addPercentCell(sheet, row, 3, vendor.slaCompliance / 100);
    addChangeCell(sheet, row, 4, vendor.changePercent);
    addDataCell(sheet, row, 5, vendor.avgResponseTime, true);
    addDataCell(sheet, row, 6, vendor.totalSpend, true);
    addPercentCell(sheet, row, 7, vendor.budgetUtilization / 100);
    addDataCell(sheet, row, 8, vendor.workOrdersTotal, true);
    addDataCell(sheet, row, 9, vendor.workOrdersCompleted, true);
    addPercentCell(sheet, row, 10, vendor.completionRate / 100);
    addDataCell(sheet, row, 11, vendor.activeVendors, true);
  });
}

function createTopVendorsTable(sheet, monthlyData) {
  // Get latest month data for vendors
  const latestMonth = monthlyData[monthlyData.length - 1];
  const vendors = latestMonth.vendorPerformance.topVendors;

  createTableHeader(
    sheet,
    ["Vendor Name", "Category", "SLA %", "Rating", "Cost (€)", "Response (h)"],
    "FFF43F5E"
  );

  vendors.forEach((vendor, index) => {
    const row = index + 2;
    addDataCell(sheet, row, 1, vendor.name);
    addDataCell(sheet, row, 2, vendor.category);
    addPercentCell(sheet, row, 3, vendor.sla / 100);
    addDataCell(sheet, row, 4, vendor.rating, true);
    addDataCell(sheet, row, 5, vendor.cost, true);
    addDataCell(sheet, row, 6, vendor.responseTime, true);
  });
}

function createPredictiveAnalyticsTable(sheet, monthlyData) {
  createTableHeader(
    sheet,
    [
      "Month",
      "Date",
      "Predictions",
      "Accuracy %",
      "Prevented (h)",
      "Savings (€)",
      "Risk Score",
      "Risk Level",
      "Next Month",
      "Next Quarter",
      "Next Year"
    ],
    "FFA855F7"
  );

  monthlyData.forEach((month, index) => {
    const row = index + 2;
    const pred = month.predictiveAnalytics;

    addDataCell(sheet, row, 1, month.displayMonth);
    addDataCell(sheet, row, 2, month.date);
    addDataCell(sheet, row, 3, pred.failurePredictions, true);
    addPercentCell(sheet, row, 4, pred.accuracyRate / 100);
    addDataCell(sheet, row, 5, pred.preventedDowntime, true);
    addDataCell(sheet, row, 6, pred.costSavings, true);
    addDataCell(sheet, row, 7, pred.riskScore, true);
    addDataCell(sheet, row, 8, pred.riskLevel);
    addDataCell(sheet, row, 9, pred.costForecast.nextMonth, true);
    addDataCell(sheet, row, 10, pred.costForecast.nextQuarter, true);
    addDataCell(sheet, row, 11, pred.costForecast.nextYear, true);
  });
}

function createUpcomingMaintenanceTable(sheet, monthlyData) {
  // Get latest month data for upcoming maintenance
  const latestMonth = monthlyData[monthlyData.length - 1];
  const maintenance = latestMonth.predictiveAnalytics.upcomingMaintenance;

  createTableHeader(
    sheet,
    [
      "Asset ID",
      "Asset Name",
      "Category",
      "Date",
      "Probability %",
      "Cost (€)",
      "Action"
    ],
    "FFDC2626"
  );

  maintenance.forEach((item, index) => {
    const row = index + 2;
    addDataCell(sheet, row, 1, item.assetId);
    addDataCell(sheet, row, 2, item.asset);
    addDataCell(sheet, row, 3, item.category);
    addDataCell(sheet, row, 4, item.date);
    addPercentCell(sheet, row, 5, item.probability / 100);
    addDataCell(sheet, row, 6, item.cost, true);
    addDataCell(sheet, row, 7, item.recommendedAction);
  });
}
